import React, {useEffect, useMemo} from 'react'
import {Progress} from '@chakra-ui/react'
import {
    Skeleton,
    HStack,
    Box,
    Flex,
    Text,
    Badge,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import PropTypes from 'prop-types'
import {useSiteContext} from '../../contexts/site'
import CloudinaryImage from '../cloudinary-media/image'
import useTagManager from '../../hooks/use-tag-manager'
import {getBasketProductIds, getGwpQualifyingSpend} from '../../utils/basket-utils'

function getGwpMeterData(currentBasket, promoConfig, promoId, promoDetails, qualifyingProductIds) {
    let maxBonusItems = 0
    let progressPercentage = 0
    let meterMessageType
    let activeLevel = 0

    const basketTotal = getGwpQualifyingSpend(currentBasket, promoId, qualifyingProductIds)

    const updatedLevels = promoConfig.levels.map((level, index) => {
        const isReached = basketTotal >= level.threshold
        const remainingAmount = isReached ? 0 : level.threshold - basketTotal

        if (isReached) {
            activeLevel = index + 1
            maxBonusItems = level.bonusProducts
        }

        return {
            ...level,
            reached: isReached,
            remaining: remainingAmount?.toFixed(2)
        }
    })

    promoConfig.gwpBonusItemsInCart = currentBasket?.bonusLineItems?.length ?? 0
    promoConfig.gwpMaxBonusItems = maxBonusItems
    promoConfig.gwpBonusItemsLeft = maxBonusItems - promoConfig.gwpBonusItemsInCart

    if (activeLevel === 0 && basketTotal === 0) {
        meterMessageType = 1
    } else if (activeLevel === 0 && basketTotal > 0) {
        meterMessageType = 2
        progressPercentage = Math.min(100, (basketTotal / promoConfig.levels[0].threshold) * 100)
    } else if (activeLevel >= 1) {
        meterMessageType = 3
        progressPercentage = 100
    }

    promoConfig.gwpMessageOverMeter = meterMessageType
    promoConfig.promotionID = promoId
    promoConfig.levels = updatedLevels
    promoConfig.allLevelsReached = updatedLevels[updatedLevels.length - 1].reached
    promoConfig.noLevelReached = !updatedLevels[0].reached
    promoConfig.currentLevelNumber = activeLevel
    promoConfig.currentLevelData = updatedLevels[activeLevel > 0 ? activeLevel - 1 : 0]
    promoConfig.numberOfLevels = updatedLevels.length

    return {
        promotionID: promoConfig.promotionID,
        type: promoConfig.meterType,
        value: promoConfig.gwpValue,
        title: promoConfig.title,
        subtitle: promoConfig.subtitle,
        message: promoConfig.gwpMessageOverMeter,
        gwpWhenYouSpendMsg: promoConfig.gwpWhenYouSpendMsg,
        gwpImageUrl: promoConfig.gwpImageUrl,
        meterColor: promoConfig.meterColor,
        meterBorderColor: promoConfig.meterBorderColor,
        meterProgress: progressPercentage,
        badgeBackgroundColor: promoConfig.badgeBackgroundColor,
        badgeColor: promoConfig.badgeColor,
        isGwpMeterEnabled: promoDetails?.c_enableProgressMeter,
        gwpMessages: promoConfig.gwpMessages,
        remaining: `$${promoConfig.currentLevelData.remaining}`
    }
}

export const GWPMeter = ({
    basket,
    isBasketLoading,
    size = 'plp',
    dataLayerLoaded = true,
    hits = [],
    product = {},
    promoDetail,
    isPromoLoading,
    qualifyingProductIds,
    isQualifyingProductsLoading
}) => {
    const {isYR} = useSiteContext()
    const tagManager = useTagManager()
    const variant = isYR ? 'yr' : 'ks'
    const styles = useMultiStyleConfig('Gwp', {size, variant})
    let hideMeter = false

    if (size === 'plp' && hits?.length && promoDetail?.id) {
        hideMeter = !hits?.some((h) =>
            h?.productPromotions?.some?.((p) => p.promotionId === promoDetail?.id)
        )
    }
    if (size === 'pdp' && product) {
        hideMeter = !product?.productPromotions?.some?.((p) => p.promotionId === promoDetail?.id)
    }
    if (promoDetail?.c_isYr && !isYR) {
        hideMeter = true
    }

    const {meterConfig} = useMemo(() => {
        try {
            const config = promoDetail?.c_bmsmAndGwpMeterConfig
                ? JSON.parse(promoDetail.c_bmsmAndGwpMeterConfig)
                : ''
            return {meterConfig: config}
        } catch (e) {
            console.warn('Error parsing meter config:', e.message)
            return {meterConfig: ''}
        }
    }, [promoDetail])

    const basketProductIds = useMemo(() => getBasketProductIds(basket), [basket?.productItems])
    const awaitingQualifiers = basketProductIds.length > 0 && isQualifyingProductsLoading
    const resolvedQualifyingProductIds = awaitingQualifiers ? undefined : qualifyingProductIds
    const meterData = meterConfig
        ? getGwpMeterData(
              basket,
              meterConfig,
              promoDetail?.id,
              promoDetail,
              resolvedQualifyingProductIds
          )
        : null

    useEffect(() => {
        if (
            !meterData ||
            isPromoLoading ||
            isBasketLoading ||
            awaitingQualifiers ||
            !dataLayerLoaded ||
            !promoDetail?.c_enableProgressMeter ||
            meterData?.type !== 'GWP'
        ) {
            return
        }
        let progress
        if (meterData?.meterProgress < 25) {
            progress = 'Empty'
        } else if (meterData?.meterProgress > 25 && meterData?.meterProgress < 99) {
            progress = 'Mid'
        } else if (meterData?.meterProgress === 100) {
            progress = 'Full'
        }
        window.__dataLayerLoaded = true
        tagManager.promoMeterImpression(
            {
                id: promoDetail?.id,
                name: promoDetail.name,
                creative: promoDetail.details,
                position: size
            },
            meterData?.type,
            progress
        )
        window.__dataLayerLoaded = false
    }, [
        promoDetail,
        isPromoLoading,
        isBasketLoading,
        awaitingQualifiers,
        meterData?.meterProgress,
        dataLayerLoaded
    ])

    if (
        !meterData ||
        !promoDetail?.c_enableProgressMeter ||
        meterData?.type !== 'GWP' ||
        hideMeter ||
        (size === 'plp' && !hits.length)
    ) {
        return null
    }

    if (isBasketLoading || isPromoLoading || awaitingQualifiers) {
        return (
            <Box {...styles.wrapper}>
                <Skeleton height={{base: '110px', md: '90px'}} width="100%" borderRadius="md" />
            </Box>
        )
    }
    const badgeStyles = !isYR
        ? {
              background: meterData?.badgeBackgroundColor || 'yellow.600',
              color: meterData?.badgeColor || 'black'
          }
        : {}
    const progressStyles = !isYR
        ? {
              sx: {
                  borderColor: meterData?.meterBorderColor || 'gray.600',
                  '& > div[role="progressbar"]': {
                      bg: meterData?.meterColor || 'yellow.600'
                  }
              }
          }
        : {}
    return (
        <>
            <Flex {...styles.wrapper}>
                {/* Image, title and badge */}
                <Box align="center" display="inline-flex" gap="15px" flexShrink={0}>
                    {meterData?.gwpImageUrl && (
                        <Box>
                            <CloudinaryImage
                                src={meterData.gwpImageUrl}
                                alt={meterData?.title || 'Promotion Image'}
                                aspectRatio="74:92"
                                imgSizes={{
                                    base: '75'
                                }}
                                sizes={[74, 148, 222]}
                                styles={{
                                    aspectRatio: '74/92',
                                    width: '74px',
                                    height: '92px',
                                    htmlWidth: 74,
                                    htmlHeight: 92
                                }}
                            />
                        </Box>
                    )}

                    <Box margin="5px 0" textAlign="left">
                        {meterData?.title && (
                            <Text
                                fontWeight="400"
                                letterSpacing="0.115px"
                                lineHeight="normal"
                                fontSize="23px"
                                fontFamily="heading"
                                maxWidth={{lg: size === 'pdp' ? '255px' : 'unset'}}
                                color="black.100"
                            >
                                {meterData?.title?.length <= 30
                                    ? meterData.title
                                    : meterData?.title?.slice(0, 30) + '...'}
                            </Text>
                        )}

                        {meterData?.gwpMessages?.value && (
                            <Badge {...styles.badge} {...badgeStyles}>
                                {meterData.gwpMessages.value}
                            </Badge>
                        )}
                    </Box>
                </Box>

                {/* Progress Bar */}
                <Box flex="1" maxWidth="100%" width="100%" margin="5px 0 0px 0">
                    <HStack spacing={1} flexWrap="wrap">
                        <Text
                            letterSpacing="0.08px"
                            lineHeight="25px"
                            fontSize="16px"
                            color={meterData?.meterProgress === 100 ? 'teal.400' : 'black.300'}
                            fontWeight={meterData?.meterProgress === 100 ? '500' : '300'}
                        >
                            {meterData?.meterProgress === 0
                                ? meterData?.gwpMessages?.spend1
                                : meterData?.meterProgress === 100
                                ? meterData?.gwpMessages?.success
                                : meterData?.gwpMessages?.away1}
                        </Text>
                        <Text
                            display={meterData?.meterProgress === 100 ? 'none' : 'block'}
                            fontWeight="500"
                            color="black.300"
                            letterSpacing="0.08px"
                            lineHeight="25px"
                        >
                            {meterData?.meterProgress
                                ? meterData?.remaining
                                : meterData?.gwpMessages?.spendValue}
                        </Text>
                        <Text
                            letterSpacing="0.08px"
                            lineHeight="25px"
                            color="black.300"
                            fontWeight="300"
                            fontSize="16px"
                        >
                            {meterData?.meterProgress === 0
                                ? meterData?.gwpMessages?.spend2
                                : meterData?.meterProgress === 100
                                ? meterData?.gwpMessages?.claim
                                : meterData?.gwpMessages?.away2}
                        </Text>
                    </HStack>
                    <Progress
                        value={meterData?.meterProgress ? meterData?.meterProgress : 2}
                        variant={variant}
                        size="gwp"
                        {...progressStyles}
                        className="progress-bar"
                    />
                    <Text
                        color="black.100"
                        fontSize="13px"
                        letterSpacing="0.065px"
                        fontWeight="300"
                        lineHeight="22px"
                    >
                        {meterData?.gwpMessages?.limit
                            ? meterData?.gwpMessages?.limit
                            : meterData?.gwpMessages?.ends}
                    </Text>
                </Box>
            </Flex>
        </>
    )
}

GWPMeter.displayName = 'GWPMeter'

GWPMeter.propTypes = {
    basket: PropTypes.object,
    searchQuery: PropTypes.any,
    hits: PropTypes.array,
    product: PropTypes.object,
    isBasketLoading: PropTypes.bool,
    dataLayerLoaded: PropTypes.bool,
    size: PropTypes.string,
    promoDetail: PropTypes.shape({
        id: PropTypes.string,
        c_bmsmAndGwpMeterConfig: PropTypes.string,
        c_enableProgressMeter: PropTypes.bool,
        c_isYr: PropTypes.bool,
        name: PropTypes.string,
        promoDetail: PropTypes.object,
        details: PropTypes.object
    }),
    isPromoLoading: PropTypes.bool,
    qualifyingProductIds: PropTypes.object,
    isQualifyingProductsLoading: PropTypes.bool
}

export default GWPMeter
